import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { Download, X, ChevronLeft, ChevronRight, Search } from 'lucide-react';
import { LoadingSpinner, ErrorDisplay } from '@/Components/common';
import { getAllOrganizations, type Organization } from '@/services/PagesServices/OrganizationService';
import {
  getLearners, getTranscript, getBulkTranscripts,
  type LearnerRow, type FullTranscript,
} from '@/services/PagesServices/LearnerTranscriptService';
import { exportTranscriptPDF, exportBulkTranscriptsPDF } from '@/utils/transcriptPdfExport';
import '../../LearnerTranscripts/LearnerTranscripts.css';

const fmtDate = (d: string | null | undefined) => {
  if (!d) return '—';
  const date = new Date(d);
  return Number.isNaN(date.getTime()) ? String(d) : date.toLocaleDateString();
};

/** Learner Transcripts — rendered as a tab inside the User Management page. */
const TranscriptsTab: React.FC = () => {
  const [orgs, setOrgs] = useState<Organization[]>([]);
  const [orgId, setOrgId] = useState<number | ''>('');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);

  const [learners, setLearners] = useState<LearnerRow[]>([]);
  const [pagination, setPagination] = useState({ total: 0, page: 1, limit: 20, totalPages: 1 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [transcript, setTranscript] = useState<FullTranscript | null>(null);
  const [loadingTranscript, setLoadingTranscript] = useState(false);
  const [exportingBulk, setExportingBulk] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    getAllOrganizations({ limit: 1000, isActive: '' }).then((res) => {
      if (res.success && res.data) setOrgs(res.data);
    });
  }, []);

  useEffect(() => {
    const id = setTimeout(() => { setDebouncedSearch(search); setPage(1); }, 350);
    return () => clearTimeout(id);
  }, [search]);

  useEffect(() => {
    setLoading(true);
    setError(null);
    getLearners({ orgId: orgId || null, search: debouncedSearch, page, limit: 20 }).then((res) => {
      if (res.success && res.data) {
        setLearners(res.data.learners);
        setPagination(res.data.pagination);
      } else {
        setError(res.error?.message ?? 'Failed to load learners');
      }
      setLoading(false);
    });
  }, [orgId, debouncedSearch, page, reloadKey]);

  const openTranscript = async (uuid: string) => {
    setLoadingTranscript(true);
    setTranscript(null);
    const res = await getTranscript(uuid);
    setLoadingTranscript(false);
    if (res.success && res.data) setTranscript(res.data);
    else toast.error(res.error?.message ?? 'Failed to load transcript');
  };

  const onExportBulk = async () => {
    if (!orgId) return;
    setExportingBulk(true);
    const res = await getBulkTranscripts(orgId);
    setExportingBulk(false);
    if (res.success && res.data) {
      if (!res.data.transcripts.length) { toast.info('No learners to export for this organization.'); return; }
      const orgName = orgs.find((o) => o.id === orgId)?.name || 'Organization';
      await exportBulkTranscriptsPDF(res.data.transcripts, orgName);
      if (res.data.truncated) toast.warn('Export capped at 200 learners.');
    } else {
      toast.error(res.error?.message ?? 'Bulk export failed');
    }
  };

  return (
    <div className="s-admin-transcripts s-admin-transcripts--embedded">
      {/* Filters */}
      <div className="s-admin-transcripts-filters">
        <div className="s-admin-transcripts-filter">
          <label>Organization</label>
          <select value={orgId} onChange={(e) => { setOrgId(e.target.value ? Number(e.target.value) : ''); setPage(1); }}>
            <option value="">All organizations</option>
            {orgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
          </select>
        </div>
        <div className="s-admin-transcripts-filter s-admin-transcripts-search">
          <label>Search</label>
          <div className="s-admin-transcripts-search-box">
            <Search size={16} />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Name or email" />
          </div>
        </div>
        <button
          className="s-admin-transcripts-bulk-btn"
          disabled={!orgId || exportingBulk}
          onClick={onExportBulk}
          title={orgId ? 'Export every learner in this organization' : 'Select an organization first'}
        >
          <Download size={16} /> {exportingBulk ? 'Building…' : 'Export all (org)'}
        </button>
      </div>

      {/* Learner table */}
      {loading ? (
        <div className="s-admin-transcripts-center"><LoadingSpinner size="md" /></div>
      ) : error ? (
        <ErrorDisplay title="Failed to load learners" message={error} type="error" showRetry onRetry={() => setReloadKey((k) => k + 1)} />
      ) : learners.length === 0 ? (
        <div className="s-admin-transcripts-empty">No learners found.</div>
      ) : (
        <div className="s-admin-transcripts-card">
          <table className="s-admin-transcripts-table">
            <thead>
              <tr><th>Name</th><th>Email</th><th>Organization</th><th></th></tr>
            </thead>
            <tbody>
              {learners.map((l) => (
                <tr key={l.uuid}>
                  <td>{l.name || '—'}</td>
                  <td>{l.email}</td>
                  <td>{l.organizations || '—'}</td>
                  <td className="s-admin-transcripts-row-action">
                    <button onClick={() => openTranscript(l.uuid)}>View transcript</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="s-admin-transcripts-pager">
            <button disabled={page <= 1} onClick={() => setPage(page - 1)}><ChevronLeft size={16} /></button>
            <span>Page {pagination.page} of {pagination.totalPages} · {pagination.total} learners</span>
            <button disabled={page >= pagination.totalPages} onClick={() => setPage(page + 1)}><ChevronRight size={16} /></button>
          </div>
        </div>
      )}

      {/* Transcript drawer */}
      {(loadingTranscript || transcript) && (
        <>
          <div className="s-admin-transcripts-overlay" onClick={() => setTranscript(null)} />
          <aside className="s-admin-transcripts-drawer">
            <div className="s-admin-transcripts-drawer-head">
              <h2>{loadingTranscript ? 'Loading…' : `${transcript?.profile.first_name || ''} ${transcript?.profile.last_name || ''}`.trim() || 'Transcript'}</h2>
              <div className="s-admin-transcripts-drawer-actions">
                {transcript && (
                  <button className="s-admin-transcripts-export-btn" onClick={() => exportTranscriptPDF(transcript)}>
                    <Download size={16} /> Export PDF
                  </button>
                )}
                <button className="s-admin-transcripts-close" onClick={() => setTranscript(null)}><X size={20} /></button>
              </div>
            </div>

            <div className="s-admin-transcripts-drawer-body">
              {loadingTranscript ? (
                <div className="s-admin-transcripts-center"><LoadingSpinner size="md" /></div>
              ) : transcript ? (
                <>
                  <div className="s-admin-transcripts-muted">{transcript.profile.email}</div>

                  <div className="s-admin-transcripts-stats">
                    {[
                      ['Courses completed', transcript.stats.completed_courses ?? '—'],
                      ['Total courses', transcript.stats.total_courses ?? transcript.courses.length],
                      ['Certificates', transcript.stats.total_certificates ?? transcript.certificates.length],
                      ['Learning hours', Math.round(Number(transcript.learningHours?.totalHours || 0))],
                    ].map(([label, value]) => (
                      <div key={label as string} className="s-admin-transcripts-stat">
                        <div className="s-admin-transcripts-stat-value">{value as React.ReactNode}</div>
                        <div className="s-admin-transcripts-stat-label">{label as string}</div>
                      </div>
                    ))}
                  </div>

                  <h3>Courses</h3>
                  {transcript.courses.length === 0 ? <p className="s-admin-transcripts-muted">No enrolled courses.</p> : (
                    <table className="s-admin-transcripts-table s-admin-transcripts-subtable">
                      <thead><tr><th>Course</th><th>Status</th><th>Progress</th><th>Enrolled</th><th>Completed</th></tr></thead>
                      <tbody>
                        {transcript.courses.map((c) => (
                          <tr key={c.course_id}>
                            <td>{c.title}</td><td>{c.status}</td><td>{c.progress ?? 0}%</td>
                            <td>{fmtDate(c.enrolled_date)}</td><td>{fmtDate(c.completed_at)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}

                  <h3>Assessments</h3>
                  {transcript.assessments.length === 0 ? <p className="s-admin-transcripts-muted">No assessments.</p> : (
                    <table className="s-admin-transcripts-table s-admin-transcripts-subtable">
                      <thead><tr><th>Assessment</th><th>Status</th><th>Score</th><th>Date</th></tr></thead>
                      <tbody>
                        {transcript.assessments.map((a, i) => (
                          <tr key={`${a.form_id}-${i}`}>
                            <td>{a.title}</td><td>{a.status}</td>
                            <td>{a.percentage != null ? `${a.percentage}%` : a.score != null ? a.score : '—'}</td>
                            <td>{fmtDate(a.submitted_at || a.assigned_at)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}

                  <h3>Certificates</h3>
                  {transcript.certificates.length === 0 ? <p className="s-admin-transcripts-muted">No certificates.</p> : (
                    <table className="s-admin-transcripts-table s-admin-transcripts-subtable">
                      <thead><tr><th>Certificate</th><th>Status</th><th>Issued</th></tr></thead>
                      <tbody>
                        {transcript.certificates.map((c, i) => (
                          <tr key={`${c.source}-${c.id}-${i}`}>
                            <td>{c.certificate_name}</td><td>{c.status}</td><td>{fmtDate(c.issued_date)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </>
              ) : null}
            </div>
          </aside>
        </>
      )}
    </div>
  );
};

export default TranscriptsTab;
